/* eslint-disable @nx/enforce-module-boundaries */
import { ApolloError, useQuery } from '@apollo/client';
import { Box, HStack, Link, Text, VStack } from '@chakra-ui/react';
import { SnippetBox } from '@gamma/ui/display';
import MonitoringSharpIcon from '@gamma/ui/icons/MonitoringSharpIcon';
import {
  GET_YARA_ALERT_METADATA,
  IGetAlertMetadata,
  IGetDetections,
  useGetDetections,
} from '../../queries';
import { AccordionPanel, Panel, PanelHeader } from '@gamma/ui/layout';
import { RouterLink } from '@gamma/ui/navigation';
import { GraphQLReqStatus, LoadingSpinner } from '@gamma/ui/progress';
import {
  DetailsGridRow,
  DetectionAssignee,
  DetectionStatus,
  IGetDetectionVariables,
  MitreTechniquesGrid,
} from '../../components';
import { stringifyAlertType } from '@investigator/shared/utils';
import { SeverityScore } from '@investigator/shared/components';
import { ROUTES } from '@investigator/shared/constants';
import { useDateRangeQueryString } from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import {
  AppConfigurationsContext,
  AuthContext,
  SlideDrawerContext,
} from '@investigator/shared/providers';
import DOMPurify from 'dompurify';
import moment from 'moment';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext } from 'react-router';
import dedent from 'ts-dedent';
import { IDetectionContext } from '../Detection';
import {
  DetectionEntities,
  DetectionNextSteps,
  GPTPrompts,
  RelatedDetections,
} from './Components';
import { DetectionSummary } from './Components/DetectionSummary';
import { Detection } from '@investigator/shared/generated/graphql';
const { systemSettingsAlertCatalog } = ROUTES;

const {
  correlationChart,
  detection: detectionText,
  detectionDetails,
} = i18n.pages.detections;
const { headings } = correlationChart;

const {
  assignee,
  alertCategory,
  significance,
  mitreTechniques,
  validation,
  nextSteps,
  description: localeDesc,
  status,
  type,
  severity,
  firstTimeSeen,
  lastTimeSeen,
  numberOfAlerts,
  yaraRuleMetaData,
} = detectionText;

export interface DetailsProps {
  detectionData?: IGetDetections;
  detectionError?: ApolloError;
  detectionLoading?: boolean;
  metadataData?: IGetAlertMetadata;
  metadataError?: ApolloError;
  metadataLoading?: boolean;
  getDetectionVariables?: IGetDetectionVariables;
}
interface DetectionDetailsProps {
  detailsProps?: DetailsProps;
}

export function safelySetInnerHTML(html: string) {
  const key = 'dangerouslySetInnerHTML';
  return {
    [key]: {
      __html: DOMPurify.sanitize(html) ?? '',
    },
  };
}

export const DetectionDetails = ({ detailsProps }: DetectionDetailsProps) => {
  const {
    detectionData,
    detectionError,
    detectionLoading,
    metadataData,
    metadataError,
    metadataLoading,
    edrConfigError,
    edrConfigLoading,
    getDetectionVariables,
  } = useOutletContext<IDetectionContext>() || {};

  const { user } = useContext(AuthContext);
  const { appConfigurationsLoading } = useContext(AppConfigurationsContext);

  const { setIsSlideDrawerOpen } = useContext(SlideDrawerContext);

  const { start, end } = useDateRangeQueryString();

  const detectionDetailsProps = useMemo(() => {
    return detailsProps
      ? {
          ...detailsProps,
        }
      : {
          detectionData,
          detectionError,
          detectionLoading,
          metadataData,
          metadataError,
          metadataLoading,
          getDetectionVariables,
        };
  }, [
    detailsProps,
    detectionData,
    detectionError,
    detectionLoading,
    metadataData,
    metadataError,
    metadataLoading,
    getDetectionVariables,
  ]);

  const [suricataRuleText, setSuricataRuleText] = useState('');

  const detectionInfo =
    detectionDetailsProps?.detectionData?.getDetections?.detections[0];

  const [selectedDetectionId, setSelectedDetectionId] = useState<string>();

  const { loading: getDetectionsLoading, data } = useGetDetections({
    fetchPolicy: 'network-only',
    skip: !selectedDetectionId,
    variables: {
      detection_ids: [
        {
          detection_id: selectedDetectionId,
          tenant: user?.attributes?.['custom:tenant_id'],
        },
      ],
    },
  });

  const [selectedDetection, setSelectedDetection] = useState<
    Detection | undefined
  >();

  useEffect(() => {
    setSelectedDetection(data?.getDetections?.detections?.[0]);
  }, [data]);

  useEffect(() => {
    setSelectedDetectionId(detectionInfo?.detection_id);
  }, [detectionInfo]);

  const {
    rank,
    alert_info,
    assignment_info,
    detection_status,
    total_alert_count,
    earliest_start_timestamp,
    latest_start_timestamp,
  } = detectionInfo || {};

  const {
    description,
    content_doc,
    mitre_mappings: metadata_mitre_mappings,
  } = detectionDetailsProps?.metadataData?.getAlertMetadata?.metadata[0] || {};

  // set panel description
  useEffect(() => {
    if (detectionDetailsProps?.metadataLoading) {
      return;
    }

    const suricataRuleText =
      detectionDetailsProps?.metadataData?.getAlertMetadata?.metadata[0]
        ?.rule ?? '';

    setSuricataRuleText(suricataRuleText);
  }, [
    description,
    detectionDetailsProps?.metadataLoading,
    alert_info?.alert_type,
    detectionDetailsProps?.metadataData?.getAlertMetadata?.metadata,
  ]);

  const hasMetaContent = !!(description || content_doc?.significance);

  const isLoading =
    edrConfigLoading ||
    appConfigurationsLoading ||
    detectionDetailsProps?.metadataLoading ||
    detectionDetailsProps?.detectionLoading;

  const nextStepsRef = useRef<HTMLDivElement>(null);

  const validationRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const skipYaraMetadata =
    alert_info?.alert_type !== 'yara_corelight' ||
    !detectionInfo?.tenant_info?.tenant_id ||
    !alert_info?.content_id;

  const {
    data: yaraMetadataData,
    loading: yaraMetadataLoading,
    error: yaraMetadataError,
  } = useQuery(GET_YARA_ALERT_METADATA, {
    skip: skipYaraMetadata,
    variables: {
      tenant: detectionInfo?.tenant_info?.tenant_id || '',
      content_id: alert_info?.content_id || '',
    },
  });

  useEffect(() => {
    if (skipYaraMetadata) {
      return;
    }
  }, [
    skipYaraMetadata,
    detectionInfo?.tenant_info?.tenant_id,
    alert_info?.content_id,
  ]);

  const yaraMatchMeta = useMemo(() => {
    if (!yaraMetadataData?.getAlertMetadata?.metadata?.[0]?.match_meta) {
      return '';
    }
    return JSON.stringify(
      yaraMetadataData.getAlertMetadata.metadata[0].match_meta,
      null,
      2,
    );
  }, [yaraMetadataData]);

  // TODO: Delete this check and any negated logic
  const isADV2 =
    alert_info?.alert_type === 'anomaly' ||
    alert_info?.alert_type === 'suricata_corelight' ||
    alert_info?.alert_type === 'yara_corelight' ||
    alert_info?.alert_type === 'notice' ||
    alert_info?.alert_type === 'ml' ||
    alert_info?.alert_type === 'custom_search_rule';

  useEffect(() => {
    return () => {
      // Reset selected detection state on unmount or navigation
      setSelectedDetectionId(undefined);
      setSelectedDetection(undefined);
    };
  }, []);

  return isLoading || detectionDetailsProps?.metadataError ? (
    <GraphQLReqStatus
      loading={!!isLoading}
      error={
        detectionDetailsProps?.detectionError ||
        detectionDetailsProps?.metadataError ||
        edrConfigError
      }
    />
  ) : (
    <Box>
      <VStack spacing={4} data-testid="detection-details-wrapper">
        {isADV2 && (
          <DetectionSummary detectionDetailsProps={detectionDetailsProps} />
        )}
        {!isADV2 && (
          <Panel>
            <PanelHeader
              borderBottomWidth="1px"
              borderBottomColor={'border.layer.1'}
            >
              <Text textStyle="h5">{detectionDetails}</Text>
            </PanelHeader>
            <HStack>
              <VStack w="315px" alignItems="start">
                <Text textStyle="body-md" color="text.secondary">
                  {status}
                </Text>
                <DetectionStatus
                  refetchMain={true}
                  detectionInfo={detectionInfo}
                  detectionStatus={detection_status}
                  getDetectionVariables={
                    detectionDetailsProps?.getDetectionVariables
                  }
                />
              </VStack>
              <VStack w="315px" alignItems="start">
                <Text textStyle="body-md" color="text.secondary">
                  {assignee}
                </Text>
                <DetectionAssignee
                  refetchMain={true}
                  detectionInfo={detectionInfo}
                  assignmentInfo={assignment_info || undefined}
                  getDetectionVariables={
                    detectionDetailsProps?.getDetectionVariables
                  }
                />
              </VStack>
            </HStack>
          </Panel>
        )}
        {!isADV2 && (
          <AccordionPanel
            w="100%"
            title="Alert Category"
            reduceMotion={true}
            storageKey="detection-details-alert-category"
          >
            <HStack gap={8} alignItems="start">
              <VStack w="70%" alignItems="start">
                {hasMetaContent &&
                  (description ?? '')?.length > 0 &&
                  alert_info?.alert_type !== 'suricata_corelight' && (
                    <DetailsGridRow
                      isDetailsPage
                      title={localeDesc}
                      data-testid="detection-description"
                    >
                      <Text>{description}</Text>
                    </DetailsGridRow>
                  )}
                {hasMetaContent && content_doc?.significance && (
                  <DetailsGridRow
                    isDetailsPage
                    title={significance}
                    data-testid="detection-significance"
                  >
                    <Text as="div">{content_doc.significance}</Text>
                  </DetailsGridRow>
                )}
                {isExpanded && hasMetaContent && content_doc?.validation && (
                  <DetailsGridRow
                    isDetailsPage
                    title={validation}
                    data-testid="detection-content-doc-validation"
                  >
                    <Box
                      ref={validationRef}
                      {...safelySetInnerHTML(content_doc?.validation)}
                      __css={{
                        ul: {
                          'margin-left': '16px',
                        },
                      }}
                    />
                  </DetailsGridRow>
                )}
                {isExpanded && hasMetaContent && content_doc?.nextsteps && (
                  <DetailsGridRow
                    isDetailsPage
                    title={nextSteps}
                    data-testid="detection-content-doc-next-steps"
                  >
                    <Box
                      ref={nextStepsRef}
                      {...safelySetInnerHTML(content_doc?.nextsteps)}
                      __css={{
                        ul: {
                          'margin-left': '16px',
                        },
                      }}
                    />
                  </DetailsGridRow>
                )}
                {alert_info?.alert_type !== 'custom_search_rule' &&
                  alert_info?.alert_type !== 'yara_corelight' && (
                    <DetailsGridRow
                      isDetailsPage
                      title=""
                      data-testid="detection-ask-gpt"
                    >
                      <GPTPrompts detailsProps={detailsProps} />
                    </DetailsGridRow>
                  )}
                {alert_info?.alert_type === 'yara_corelight' && (
                  <DetailsGridRow
                    isDetailsPage
                    title={yaraRuleMetaData}
                    data-testid="detection-yara-snippet"
                  >
                    {yaraMetadataLoading ? (
                      <LoadingSpinner />
                    ) : yaraMetadataError ? (
                      <Text color="red.300">
                        Error loading Yara metadata: {yaraMetadataError.message}
                      </Text>
                    ) : yaraMatchMeta ? (
                      <SnippetBox
                        snippet={dedent`${yaraMatchMeta}`}
                        isCopyable={true}
                      />
                    ) : (
                      <Text>No match_meta found for this Yara alert.</Text>
                    )}
                  </DetailsGridRow>
                )}

                {hasMetaContent &&
                  (content_doc?.nextsteps || content_doc?.validation) && (
                    <Link
                      color="text.link"
                      onClick={() => setIsExpanded(!isExpanded)}
                    >
                      {!isExpanded ? 'See More' : 'See Less'}
                    </Link>
                  )}
              </VStack>
              <VStack alignItems="start">
                {alert_info?.alert_name && (
                  <DetailsGridRow
                    isDetailsPage
                    data-testid="detection-alert-name"
                    title={alertCategory}
                  >
                    <Box
                      __css={{
                        wordWrap: 'anywhere',
                        _hover: { textDecoration: 'underline' },
                      }}
                    >
                      <RouterLink
                        color="text.link"
                        __css={{
                          wordWrap: 'anywhere',
                        }}
                        data-testid="pivot-detection-alert-catalog-link"
                        to={`${systemSettingsAlertCatalog(
                          `/${alert_info?.content_id}?start=${start}&end=${end}`,
                        )}`}
                      >
                        {alert_info?.alert_name}
                      </RouterLink>
                    </Box>
                  </DetailsGridRow>
                )}
                {rank?.severity !== null && rank?.severity !== undefined && (
                  <DetailsGridRow
                    isDetailsPage
                    data-testid="detection-alert-severity-score"
                    title={severity}
                  >
                    <SeverityScore score={rank.severity} />
                  </DetailsGridRow>
                )}
                {total_alert_count !== null && (
                  <DetailsGridRow
                    isDetailsPage
                    title={numberOfAlerts}
                    data-testid="detection-alert-count"
                  >
                    <Text>{total_alert_count}</Text>
                  </DetailsGridRow>
                )}
                {earliest_start_timestamp && (
                  <DetailsGridRow
                    isDetailsPage
                    title={firstTimeSeen}
                    data-testid="detection-first-timestamp"
                  >
                    <Text>
                      {moment
                        .unix(earliest_start_timestamp)
                        .format('MMMM Do, h:mma')}
                    </Text>
                  </DetailsGridRow>
                )}
                {latest_start_timestamp && (
                  <DetailsGridRow
                    isDetailsPage
                    title={lastTimeSeen}
                    data-testid="detection-last-timestamp"
                  >
                    <Text>
                      {moment
                        .unix(latest_start_timestamp)
                        .format('MMMM Do, h:mma')}
                    </Text>
                  </DetailsGridRow>
                )}
                {alert_info?.alert_type && (
                  <DetailsGridRow
                    title={type}
                    isDetailsPage
                    data-testid="detection-type"
                  >
                    <Text>{stringifyAlertType(alert_info?.alert_type)}</Text>
                  </DetailsGridRow>
                )}
                {!!metadata_mitre_mappings?.tactics?.length && (
                  <VStack alignItems="start">
                    <Text
                      color="text.secondary"
                      data-testid={'metadata_mitre_mappings-label'}
                    >
                      {mitreTechniques}
                    </Text>
                    <MitreTechniquesGrid
                      tactics={metadata_mitre_mappings?.tactics}
                    />
                  </VStack>
                )}
              </VStack>
            </HStack>
          </AccordionPanel>
        )}
        <DetectionEntities isADV2={isADV2} detectionInfo={detectionInfo} />
        {isADV2 && (
          <DetectionNextSteps
            detailsProps={detailsProps}
            nextSteps={content_doc?.nextsteps ?? ''}
          />
        )}
        {detectionInfo && (
          <AccordionPanel
            w="100%"
            reduceMotion={true}
            title={
              <HStack>
                {isADV2 && <MonitoringSharpIcon />}
                <Text>{headings.main}</Text>
                <Text fontWeight="400" color="text.secondary">
                  {headings.thirtyDays}
                </Text>
              </HStack>
            }
            storageKey="detection-details-chart"
          >
            <RelatedDetections
              detection={detectionInfo}
              suricataRuleText={suricataRuleText}
              selectedDetection={selectedDetection}
              getDetectionsLoading={getDetectionsLoading}
              setIsSlideDrawerOpen={setIsSlideDrawerOpen}
              selectedDetectionId={selectedDetectionId}
              setSelectedDetectionId={setSelectedDetectionId}
            />
          </AccordionPanel>
        )}
      </VStack>
    </Box>
  );
};
